import { useState } from 'react'
import { useLoaderData } from 'react-router-dom'
import { Button, Card, Divider, Space, Tag, Typography, message } from 'antd'
import { SafetyCertificateOutlined, SwapRightOutlined } from '@ant-design/icons'
import { css } from '@emotion/react'
import {setUser} from '@/biz/user.mjs'

const { Title, Text, Paragraph } = Typography

const readLocalStorage = (key) => {
    try { return JSON.parse(window.localStorage.getItem(key)) } catch { return null }
}

/** 解析 JWT payload 中的 expires 字段，判断是否过期 */
const isTokenExpired = (jwt) => {
    try {
        const base64Url = jwt.split('.')[1]
        // JWT 使用 base64url 编码，需转为标准 base64 后解码
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const payload = JSON.parse(atob(base64))
        return Date.now() > payload.expires
    } catch { return true } // 解析失败视为过期
}

// ── 样式 ──
const wrap = css({ display:'flex', minHeight:'100vh', justifyContent:'center', alignItems:'center', background:'linear-gradient(135deg,#f5f7fa,#c3cfe2)' })
const card = css({ width:480, borderRadius:12, boxShadow:'0 8px 32px rgba(0,0,0,0.12)' })
const logo = css({ fontSize:48, textAlign:'center', marginBottom:8 })
const appRow = css({ display:'flex', alignItems:'center', justifyContent:'center', gap:16, margin:'16px 0' })
const appBox = css({ textAlign:'center', padding:'12px 20px', background:'#f5f5f5', borderRadius:8, minWidth:120 })
const arrow = css({ fontSize:24, color:'#999' })

const scopeLabels = {
    user_info: '获取您的用户信息（昵称、手机号等）',
    project_read: '读取您的项目列表数据',
}

/**
 * OAuth 授权桥接页
 *
 * 流程：
 * 1. 检查登录状态 → 未登录去登录页
 * 2. 已登录 → 展示授权确认卡片
 * 3. 用户点击"同意授权" → AJAX 调后端生成 code → 跳转回 ratopai
 */
export const useRoute = () => {
    const loader = ({ request: { url } }) => {
        const { searchParams } = new URL(url)
        const user = readLocalStorage('user')
        const token = readLocalStorage('token')

        if (!user || !token?.token || isTokenExpired(token.token)) {
            // 清除 localStorage 和内存中的 user/token
            window.localStorage.removeItem('token')
            window.localStorage.removeItem('user')
            setUser(null)
            // 使用 window.location.href 触发完整页面重载，确保 user.mjs 模块变量重新初始化
            // （不能用 redirect()，因为客户端导航不会重载模块，导致 useRoutesLogin.useUser() 读到旧 user）
            const returnUrl = '/oauth/authorize?' + searchParams.toString()
            window.location.href = '/login?r=' + encodeURIComponent(returnUrl)
            // 阻止 React Router 继续处理 loader
            throw new Response(null, {status: 302})
        }

        // 返回 OAuth 参数给组件渲染授权确认页
        return {
            clientId: searchParams.get('client_id') || '',
            redirectUri: searchParams.get('redirect_uri') || '',
            state: searchParams.get('state') || '',
            scope: searchParams.get('scope') || '',
            user,
            token,
        }
    }
    return { loader }
}

export default function OAuthAuthorize() {
    const oauthData = useLoaderData()
    const [loading, setLoading] = useState(false)

    if (!oauthData) return null

    const { clientId, redirectUri, state, scope, user, token } = oauthData
    const scopes = scope ? scope.split(/[\s+]+/).filter(Boolean) : ['user_info', 'project_read']

    const handleAuthorize = async () => {
        if (isTokenExpired(token.token)) {
            message.warning('登录已过期，请重新登录')
            window.localStorage.removeItem('token')
            window.localStorage.removeItem('user')
            setUser(null)
            const returnUrl = '/oauth/authorize?' + new URLSearchParams({ client_id: clientId, redirect_uri: redirectUri, state, scope }).toString()
            window.location.href = '/login?r=' + encodeURIComponent(returnUrl)
            return
        }
        setLoading(true)
        try {
            const backendParams = new URLSearchParams({
                client_id: clientId,
                redirect_uri: redirectUri,
                response_type: 'code',
                state,
                scope,
                __TOKEN__: token.token,
                format: 'json',
            })
            const backendBase = import.meta.env.VITE_HTTP_BASE_URL
            const backendUrl = backendBase + '/oauth/authorize?' + backendParams.toString()

            const resp = await fetch(backendUrl, { redirect: 'manual' })

            if (resp.type === 'opaqueredirect' || resp.status === 302) {
                const loc = resp.headers.get('Location')
                if (loc) window.location.href = loc
                return
            }

            const data = await resp.json()
            if (data.redirectUrl) {
                window.location.href = data.redirectUrl
            } else if (data.code && data.code !== '00000000') {
                // 后端返回了业务错误，显示具体错误信息
                message.error(data.message || '授权失败，请重试')
            } else {
                message.error('授权失败，请重试')
            }
        } catch (e) {
            console.error('授权请求失败:', e)
            message.error('授权请求失败')
        } finally {
            setLoading(false)
        }
    }

    const handleCancel = () => {
        const cancelUrl = redirectUri
            + (redirectUri.includes('?') ? '&' : '?')
            + 'error=access_denied'
            + (state ? '&state=' + state : '')
        window.location.href = cancelUrl
    }

    return (
        <div css={wrap}>
            <Card css={card} bodyStyle={{ padding: '32px 40px' }}>
                <div css={logo}><SafetyCertificateOutlined style={{ color: '#1890ff' }} /></div>
                <Title level={4} style={{ textAlign: 'center', marginBottom: 4 }}>授权确认</Title>

                {/* 应用间授权示意 */}
                <div css={appRow}>
                    <div css={appBox}>
                        <Text strong>RaTopAI</Text>
                        <br/><Text type="secondary" style={{ fontSize: 12 }}>爱捷思平台</Text>
                    </div>
                    <SwapRightOutlined css={arrow} />
                    <div css={appBox}>
                        <Text strong>SpecTopAI</Text>
                        <br/><Text type="secondary" style={{ fontSize: 12 }}>爱捷思平台</Text>
                    </div>
                </div>

                <Paragraph type="secondary" style={{ textAlign: 'center', fontSize: 13, marginBottom: 20 }}>
                    <strong>RaTopAI</strong> 将获得以下权限
                </Paragraph>

                {/* 权限列表 */}
                <div style={{ background: '#fafafa', borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>
                    {scopes.map(s => (
                        <div key={s} style={{ padding: '6px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Tag color="blue">{s}</Tag>
                            <Text style={{ fontSize: 13 }}>{scopeLabels[s] || s}</Text>
                        </div>
                    ))}
                </div>

                {/* 当前账号 */}
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        授权账号：<Text strong>{user?.nickname || user?.name || user?.pkid || '未知'}</Text>
                    </Text>
                </div>

                {/* 按钮 */}
                <Space style={{ width: '100%', justifyContent: 'center' }}>
                    <Button onClick={handleCancel}>拒绝</Button>
                    <Button type="primary" onClick={handleAuthorize} loading={loading} icon={<SafetyCertificateOutlined />}>
                        同意授权
                    </Button>
                </Space>

                <Divider plain style={{ fontSize: 13, color: '#999' }}>or</Divider>

                <div style={{ textAlign: 'center' }}>
                    <Button type="link" onClick={() => {
                        window.localStorage.removeItem('token')
                        window.localStorage.removeItem('user')
                        setUser(null)
                        const returnUrl = '/oauth/authorize?' + new URLSearchParams({ client_id: clientId, redirect_uri: redirectUri, state, scope }).toString()
                        window.location.href = '/login?r=' + encodeURIComponent(returnUrl)
                    }}>
                        切换登录帐号
                    </Button>
                </div>
            </Card>
        </div>
    )
}
